import { AlgoliaProvider } from "@components/algolia/AlgoliaProvider";
import { HeroPLP } from "@components/cms/HeroPLP/HeroPLP";
import { renderRichTextToComponent } from "@components/cms/rich-text-renderer/renderRichTextToComponent";
import { Banner } from "@components/commerce/categories/banner";
import { Breadcrumbs } from "@components/commerce/categories/breadcrumbs/Breadcrumbs";
import { ProductListing } from "@components/commerce/product-listing/ProductListing";
import { FiltersController } from "@components/commerce/product-listing/filters/context/FiltersContextProvider";
import { PageSeo } from "@components/seo/PageSeo";
import { getSiteSettings } from "@config/site/site-api-settings";
import { getPageContentBySlug } from "@contentful-api/queries/pagePath/pagePathService";
import { getSiteSettingsContent } from "@contentful-api/queries/siteSettings/siteSettingsService";
import type {
    IModuleHero,
    IPageProductListingPage,
    ISiteSettings,
} from "@contentful-api/types/contentful";
import type { Document } from "@contentful/rich-text-types";
import { CommonCMS } from "@lib/constants/contentful";
import type { PagePath } from "@lib/constants/pagePaths";
import { getPagePathByLocale } from "@lib/constants/pagePaths";
import { COMMON_NEXT_CACHE_TTL } from "@lib/constants/revalidation";
import { MicrocopyProvider, useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { GetLiveUpdates } from "@lib/contentful/modules-renderer/ModulesRenderer";
import { PageTypes } from "@lib/enums/PageTypes";
import { LogTag, Logger, ServiceType } from "@lib/monitoring/logger";
import { getCompleteSlug } from "@lib/utils/getCompleteSlug";
import { getTopLevelCategoryName } from "@lib/utils/getTopLevelCategory";
import { getAdditionalSizesFilters } from "@lib/utils/plp/plpShoeSizes";
import { getInitialFiltersList } from "@lib/utils/productListingPageUtils";
import { ZustandProvider, useStore } from "@store/index";
import { PageHeading } from "@ui/components/content/page-heading/PageHeading";
import { Box } from "@ui/components/layout/box/Box";
import { deserialize, serialize } from "@ungap/structured-clone";
import { isEqual } from "instantsearch.js/es/lib/utils";
import { useRouter } from "next/router";
import type { GetStaticPropsContext, PreviewData } from "next/types";
import type { ParsedUrlQuery } from "querystring";
import { useEffect, type FC } from "react";
import { renderToString } from "react-dom/server";
import { InstantSearchSSRProvider, getServerState } from "react-instantsearch";

export type ProductListingPageProps = Awaited<
    ReturnType<typeof generateGetStaticPropsForProductListingPage>
>["props"] & {
    isMenPage?: boolean;
};

export const ProductListingPage: FC<ProductListingPageProps> = ({
    serializedSiteSettings,
    serializedPage,
    pathName,
    algoliaFallbackState,
    initialZustandState,
    isMenPage,
}) => {
    const page: IPageProductListingPage = deserialize(serializedPage);
    const { query, locale, asPath, isPreview } = useRouter();
    const updatedData = isPreview ? (GetLiveUpdates(page) as IPageProductListingPage) : page;
    const siteSettings: ISiteSettings = deserialize(serializedSiteSettings);
    const { getMultiple } = useMicrocopy();
    const title = updatedData?.fields?.title || "";
    const description = updatedData?.fields?.desc;
    const plpHeroImage = updatedData?.fields?.plpHeroImage;
    const listOfFilters = [
        ...(updatedData?.fields?.filtersList ?? []),
        ...getAdditionalSizesFilters(updatedData?.fields?.filtersList),
    ];

    const widgetSize = updatedData?.fields?.widgetSize || "Small";
    const titleSize = updatedData?.fields?.titleSize || "Extra Large";
    const descriptionSize = updatedData?.fields?.descriptionSize || "Normal";
    const editorialWidgets = updatedData?.fields?.editorialWidgets;
    const overlay = updatedData.fields.overlay;
    const legalText = updatedData.fields.legalText;
    const colorText = updatedData?.fields?.colorText;
    const callToActions = updatedData?.fields?.callToActions || [];
    const sortByOptionsList = updatedData?.fields?.sortByOptionsList || [];
    const bannerItems = updatedData?.fields?.categoriesBanner?.fields?.links || [];
    const seoMetadata = updatedData?.fields?.seoMetadata;
    const microcopies = (() => {
        return getMultiple(CommonCMS.shared, [
            CommonCMS.generic.readMore,
            CommonCMS.generic.readLess,
        ]);
    })();

    const setInitialFilters = useStore((state) => state.setInitialFilters);
    const initialFilters = useStore((state) => state.data.products.initialFilters);
    const productResultsCount = useStore((state) => state.data.products.resultsCount);
    useEffect(() => {
        if (!isEqual(initialFilters, initialZustandState?.products?.initialFilters)) {
            setInitialFilters(initialZustandState?.products?.initialFilters);
        }
    }, [initialZustandState?.products?.initialFilters]);

    const lastLoadedPage = useStore((state) => state.data.products.lastLoadedPage);

    return (
        <>
            {seoMetadata && <PageSeo seoModule={seoMetadata} pathName={pathName} />}
            <Breadcrumbs
                marginY={6}
                pl={[3, 3, 4, 6]}
                items={[
                    getTopLevelCategoryName(pathName),
                    ...(Array.isArray(query.categories) ? query.categories : []),
                ]}
                zIndex={3}
                color={"black"}
            />
            <main>
                {plpHeroImage ? (
                    <Box position={"relative"} mb="4">
                        <HeroPLP
                            module={
                                {
                                    fields: {
                                        title,
                                        description: renderRichTextToComponent(description),
                                        image: plpHeroImage,
                                        resultsCount: productResultsCount,
                                        imageSize: "Large",
                                        theme: "Dark",
                                        legalText,
                                        callToActions,
                                        backgroundColor: { value: "white" },
                                    },
                                } as unknown as IModuleHero
                            }
                            color={colorText}
                            index={0}
                            overlay={overlay}
                        />
                    </Box>
                ) : (
                    <PageHeading
                        title={title}
                        description={renderRichTextToComponent(description)}
                        showMoreLabel={microcopies[CommonCMS.generic.readMore]}
                        showLessLabel={microcopies[CommonCMS.generic.readLess]}
                        id={updatedData?.sys?.id}
                        titleSize={titleSize}
                        descriptionSize={descriptionSize}
                        resultsCount={productResultsCount}
                    />
                )}
                <Box ml={[3, 3, 4, 6]}>
                    <Banner bannerItems={bannerItems} />
                </Box>
                <InstantSearchSSRProvider {...algoliaFallbackState}>
                    <AlgoliaProvider locale={locale} pagePath={asPath}>
                        <FiltersController
                            listOfFilters={listOfFilters}
                            sortByOptionsList={sortByOptionsList}
                            isMenPage={isMenPage}
                            pageCategory={decodeURIComponent(
                                asPath.slice(
                                    1,
                                    asPath.indexOf("?") === -1 ? undefined : asPath.indexOf("?")
                                )
                            ).replaceAll("/", ">")}
                            page={lastLoadedPage}
                        >
                            <ProductListing
                                listOfFilters={listOfFilters}
                                authorizationInvitation={
                                    siteSettings?.fields?.authorizationInvitation
                                }
                                locale={locale}
                                widgetSize={widgetSize}
                                editorialWidgets={editorialWidgets}
                            />
                        </FiltersController>
                    </AlgoliaProvider>
                </InstantSearchSSRProvider>
            </main>
        </>
    );
};

export const generateGetStaticPropsForProductListingPage = async (
    { locale, params }: GetStaticPropsContext<ParsedUrlQuery, PreviewData>,
    path: PagePath
) => {
    const startTime = performance.now();

    const [siteSettings, pagePath] = await Promise.all([
        getSiteSettingsContent({
            locale,
            localeTagId: getSiteSettings.contentfulLocaleTagId,
            pageType: PageTypes.PLP,
            selectedFields: ["fields.sizeGuideModal"],
            microcopySetKey: "plp",
        }),
        getPageContentBySlug({
            locale,
            localeTagId: getSiteSettings.contentfulLocaleTagId,
            pageType: PageTypes.PLP,
            basePath: path,
            slug: params?.categories,
        }),
    ]);

    if (!pagePath) {
        return {
            notFound: true,
            revalidate: COMMON_NEXT_CACHE_TTL,
        };
    }

    const { content, sharedMicrocopySet, pageMicrocopySet } = siteSettings ?? {};
    const pageData = pagePath.fields.page as IPageProductListingPage;

    // Serialize Contentful data to remove circular references and ensure it's JSON-safe for Next.js
    const serializedSiteSettings = serialize(content);
    const serializedSharedMicrocopySet = serialize(sharedMicrocopySet);
    const serializedPageMicrocopySet = serialize(pageMicrocopySet);
    const serializedSizeGuideModal = serialize(content.fields.sizeGuideModal);
    const serializedPage = serialize(pageData);
    const pathName = getCompleteSlug(params?.categories, getPagePathByLocale(path, locale));

    const algoliaStartTime = performance.now();
    const filtersList = [
        ...(pageData?.fields?.filtersList ?? []),
        ...getAdditionalSizesFilters(pageData?.fields?.filtersList),
    ];
    const SSRFiltersList = filtersList.reduce((acc, it) => {
        // Assigning to accumulator is valid
        // eslint-disable-next-line no-param-reassign
        acc[it.fields?.facetName] = [];
        return acc;
    }, {});
    const instantSearchServerState = await getServerState(
        <ZustandProvider initialState={{ products: { initialFilters: SSRFiltersList } }}>
            <MicrocopyProvider
                value={{
                    shared: deserialize(serializedSharedMicrocopySet),
                    page: deserialize(serializedPageMicrocopySet),
                }}
            >
                <AlgoliaProvider locale={locale}>
                    <FiltersController
                        listOfFilters={filtersList}
                        sortByOptionsList={pageData?.fields?.sortByOptionsList}
                        pageCategory={pathName.slice(1).replaceAll("/", ">")}
                    >
                        <ProductListing
                            listOfFilters={filtersList}
                            authorizationInvitation={{} as Document}
                            locale={locale}
                        />
                    </FiltersController>
                </AlgoliaProvider>
            </MicrocopyProvider>
        </ZustandProvider>,
        {
            renderToString,
        }
    );
    const initialFiltersList = getInitialFiltersList(
        instantSearchServerState,
        filtersList.map((it) => it.fields?.facetName)
    );
    const mappedFilters = filtersList.reduce((acc, filter) => {
        acc[filter.fields.filterName] = initialFiltersList[filter.fields.facetName]?.map((it) => ({
            value: it,
            label: it,
        }));
        return acc;
    }, {});
    Logger.info(ServiceType.ALGOLIA, "PLP Algolia Query", {
        tag: LogTag.PERFORMANCE,
        duration: performance.now() - algoliaStartTime,
        pageType: PageTypes.PLP,
    });

    Logger.info(ServiceType.NEXT_API, "PLP Total Time", {
        tag: LogTag.PERFORMANCE,
        duration: performance.now() - startTime,
        pageType: PageTypes.PLP,
    });

    const props = {
        serializedSiteSettings,
        serializedSharedMicrocopySet,
        serializedPageMicrocopySet,
        serializedSizeGuideModal,
        serializedPage,
        pathName,
        pageType: PageTypes.PLP,
        initialZustandState: {
            products: {
                list: [],
                currentFilters: [],
                resultsCount: null,
                lastVisitedPlp: null,
                algoliaQueryID: null,
                initialFilters: mappedFilters,
            },
        },
        algoliaFallbackState: instantSearchServerState,
    };

    return {
        props,
        revalidate: COMMON_NEXT_CACHE_TTL,
    };
};

export const generateProductListingPageStaticPaths = async () => {
    return {
        paths: [],
        fallback: "blocking",
    };
};
